<template>
	<Breadcrumb v-show="imVisible" :home="home" :model="items">
		<template #item="{ item }">
			<a href="#" @click="breadActions(item)">
				<i v-if="item.icon != null" :class="item.icon" style="margin-right: 0.25rem"></i>
				<span v-else
					><span v-if="item.highlighted" style="font-weight: bold !important">{{ item.label }}</span
					><span v-else>{{ item.label }}</span>

					<span v-if="item.locked" class="ml-2">
						<span v-tooltip="lock.tooltip">
							<FontAwesomeIcon :icon="['fas', 'lock-keyhole']" class="text-yellow-400" />
						</span>
					</span>
				</span>
			</a>
		</template>
	</Breadcrumb>
</template>
<!-- style="color: black !important; background-color: white !important; border-collapse: collapse" -->
<script setup lang="ts">
import { ref, onMounted } from "vue"
import Breadcrumb from "primevue/breadcrumb"
import { EventBus } from "../eventBus"
import { useRouter, useRoute } from "vue-router"
import { ApiClient } from "../../entities/NSwagGenerated"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faLockKeyhole } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { useAssessmentStore } from "@/store/assessmentStore"
import { useAssessmentLock } from "@/composables/assessmentLock"

const assessmentStore = useAssessmentStore()
const lock = useAssessmentLock(assessmentStore)
library.add(faLockKeyhole)
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const imVisible = ref(false)
const router = useRouter()
const home = ref({
	icon: "pi pi-home",
	to: "projectList",
	type: "home",
})
interface BreadcrumbItem {
	label: string
	key: string
	type: string
	icon?: string
	highlighted?: boolean
	locked?: boolean
	lockedBy?: string
}

const items = ref<BreadcrumbItem[]>([])

interface BreadcrumbData {
	type: "project" | "subproject" | "assessment" | "result"
	label: string
	id: string
}
const indexMap: Record<BreadcrumbData["type"], number> = {
	project: 0,
	subproject: 1,
	assessment: 2,
	result: 3,
}
onMounted(() => {
	EventBus.on("showBreadcrumb", (subCommand) => {
		switch (subCommand) {
			case "reset":
				items.value = []
				break
			default:
		}
		imVisible.value = true
	})
	EventBus.on("hideBreadcrumb", () => {
		items.value = []
		imVisible.value = false
	})
	EventBus.on("buildBreadcrumb", (data: any) => {
		items.value = []
		imVisible.value = true
		switch (data.type) {
			case "subproject":
				apiClient.getSingleProjectById(data.id).then((res) => {
					EventBus.emit("updateBreadcrumb", {
						type: "project",
						label: res.projectName,
						id: res.id,
						highlighted: true,
					})
				})
				break
			case "assessment":
				apiClient.getSinglesubProjectById(data.id).then((res) => {
					EventBus.emit("updateBreadcrumb", {
						type: "project",
						label: res.project?.projectName,
						id: res.project?.id,
						highlighted: false,
					})
					EventBus.emit("updateBreadcrumb", {
						type: "subproject",
						label: res.subProjectName,
						id: res.id,
						highlighted: true,
					})
				})
				break
			case "theassessment":
				apiClient.getSingleAssessmentById(data.id).then((res) => {
					EventBus.emit("updateBreadcrumb", {
						type: "project",
						label: res.subProject?.project?.projectName,
						id: res.subProject?.project?.id,
						highlighted: false,
					})
					EventBus.emit("updateBreadcrumb", {
						type: "subproject",
						label: res.subProject?.subProjectName,
						id: res.subProject?.id,
						highlighted: false,
					})
					EventBus.emit("updateBreadcrumb", {
						type: "assessment",
						label: res.assessmentName,
						id: res.id,
						highlighted: true,
						locked: res.isLocked,
						lockedBy: res.lockedByPerson?.firstName + " " + res.lockedByPerson?.lastName || null,
					})
				})
				break
		}
	})
	EventBus.on("updateBreadcrumb", (data: any) => {
		const index = indexMap[data.type as BreadcrumbData["type"]] || 0

		if (index - items.value.length == 1) return
		if (items.value.length - 1 < index) {
			items.value.push({
				label: data.label,
				key: data.id,
				type: data.type,
				icon: undefined,
				highlighted: data.highlighted,
				locked: data.locked || false,
				lockedBy: data.lockedBy || "",
			})
		} else {
			items.value[index] = {
				label: data.label,
				key: data.id,
				type: data.type,
				icon: undefined,
				highlighted: data.highlighted,
				locked: data.locked || false,
				lockedBy: data.lockedBy || "",
			}
		}
	})
	EventBus.on("actionInBreadcrumb", (data: any) => {
		breadActions(data)
	})
	EventBus.on("removeFromBreadcrumb", (data: any) => {
		const index = indexMap[data.type as BreadcrumbData["type"]] || 0
		items.value.splice(index + 1)
	})
	EventBus.on("renameInBreadcrumb", (data: any) => {
		const index = indexMap[data.type as BreadcrumbData["type"]] || 0
		if (items.value.length > index) {
			items.value[index] = {
				label: data.label,
				key: items.value[index].key,
				type: data.type,
				icon: undefined,
				highlighted: items.value[index].highlighted,
				locked: items.value[index].locked,
				lockedBy: items.value[index].lockedBy,
			}
		}
	})
	items.value = []
})

function breadActions(action: any) {
	const index = indexMap[action.type as BreadcrumbData["type"]] || 0
	switch (action.type) {
		case "home":
			router.push({ name: "projectList" })
			break
		case "project":
			router.push({ name: "subProjectList", query: { projectId: action.key } })
			items.value.splice(index + 1)
			break
		case "subproject":
			router.push({ name: "assessmentList", query: { subProjectId: action.key } })
			items.value.splice(index + 1)
			break
	}
}
</script>
<style scoped>
.p-breadcrumb {
	color: var(--p-breadcrumb-item-color);
	background-color: var(--p-breadcrumb-background);
	border-radius: 3px;
	border: 1px solid var(--p-breadcrumb-border-color);
	padding: 1rem;
}
.pi-home,
.p-breadcrumb i {
	color: var(--p-breadcrumb-item-icon-color);
}
/* Ensure separators follow the item color */
.p-breadcrumb :deep(.p-breadcrumb-separator),
.p-breadcrumb :deep(.p-breadcrumb-chevron),
.p-breadcrumb :deep(.p-menuitem-separator) {
	color: var(--p-breadcrumb-item-color) !important;
}
</style>
